/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { array, func, object, string } from 'prop-types';

import { getBrandName } from '../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import useExperience from '../../../../helpers/Personalization/useExperience';
import useClickStreamCustomEventsTracking from '../../../../helpers/Personalization/useClickstreamCustomEventsTracking';
import {
    getUniqueTrackingProducts,
    mapExperienceProductToRecipientProduct,
    mapExperienceProductToTrackingProduct,
} from '../../utils/giftListRecommendations';
import EventDispatcher from '../../state/reducers/EventDispatcher';

const INTERACTION_NAME = 'harryanddavid-Registered-Recommendations-Gift-List-v2-1';

const RecipientRecommendationLoader = ({
    occasionId, occasionName, onTrackingReady, recipient,
}) => {
    const dispatch = useDispatch();
    const brand = useSelector(getBrandName) || 'harryanddavid';
    const anchorPartNumber = recipient?.products?.[0]?.oGPartNumber || '';

    const {
        data, error, loading, variables,
    } = useExperience({
        interactionName: INTERACTION_NAME,
        targeting: { pageType: 'gift-list' },
        type: 'product',
        resolveIdentity: true,
        queryName: 'GiftListRegisteredRecommendation',
        userAttributes: [
            { name: 'currentBrand', value: brand },
            { name: 'isSympathy', value: 'false' },
            { name: 'gfOccasion', value: occasionName },
            { name: 'referenceProductId', value: anchorPartNumber },
        ],
    });

    // Each Apollo result includes both campaign metadata and Vertex products.
    // Report the settled result to the parent so it can publish one page-level
    // impression with products from all current recipient loaders.
    useEffect(() => {
        if (loading || variables?.skip || !recipient?.id || (!data?.experience && !error)) return;

        const experimentTracking = data?.experience?.experimentTracking;
        onTrackingReady({
            experimentTracking,
            recipientItemId: recipient.id,
            products: (data?.experience?.products || []).map(mapExperienceProductToTrackingProduct),
        });
    }, [data, error, loading, variables?.skip, recipient?.id, onTrackingReady]);

    // SF products are merged into recipient.products inside Services.js's initial fetch; Vertex products are merged here.
    // When this recipient's recommendations come back, swap them into redux for this
    // recipient only. Skip if nothing returned or if the override is already in place.
    useEffect(() => {
        const experienceProducts = data?.experience?.products || [];
        if (!experienceProducts.length || !recipient?.id) return;

        // If this response is already in redux, do not dispatch the override again.
        // When tracking exists, also make sure that metadata has been applied.
        const experimentTracking = data?.experience?.experimentTracking;
        const alreadyApplied = recipient.products?.[1]?.oGPartNumber === experienceProducts[0]?.partNumber
            && (!experimentTracking?.campaignId || recipient.products?.[1]?.experimentTracking?.campaignId === experimentTracking.campaignId);
        if (alreadyApplied) return;

        // Map campaign metadata once for the whole experience response.
        // Keep it with the mapped products so existing gift-list click tracking can use it later.
        const mappedProducts = experienceProducts.map((experienceProduct) => ({
            ...mapExperienceProductToRecipientProduct(experienceProduct),
            ...(experimentTracking?.campaignId ? { experimentTracking } : {}),
        }));

        // Preserve the legacy recipient product shape, with optional GB tracking attached for click events.
        dispatch(EventDispatcher.applyGiftListRecsOverride({
            mappedProducts,
            occasionId,
            recipientItemId: recipient.id,
        }));
    }, [data, recipient, occasionId, dispatch]);

    return null;
};

RecipientRecommendationLoader.defaultProps = {
    occasionId: '',
    occasionName: '',
    onTrackingReady: () => {},
    recipient: {},
};

RecipientRecommendationLoader.propTypes = {
    occasionId: string,
    occasionName: string,
    onTrackingReady: func,
    recipient: object,
};

const WithRegisteredRecommendations = ({ occasionId, occasions, recipients }) => {
    const [trackingResultsByRecipient, setTrackingResultsByRecipient] = useState({});
    const recipientList = recipients?.[occasionId] || [];
    const recipientIds = recipientList.map((recipient) => recipient.id).filter(Boolean);
    const recipientIdsKey = recipientIds.join('|');
    const completedRecipientCount = recipientIds.filter((recipientId) => trackingResultsByRecipient[recipientId]).length;

    // Build the one page-level impression payload from the settled Apollo results.
    // useMemo keeps the payload stable between renders so the tracking hook only
    // fires after all current recipient loaders have reported their products.
    const impressionTracking = useMemo(() => {
        if (!recipientIds.length || completedRecipientCount !== recipientIds.length) return null;

        const experimentTracking = recipientIds
            .map((recipientId) => trackingResultsByRecipient[recipientId]?.experimentTracking)
            .find((tracking) => tracking?.campaignId);
        if (!experimentTracking?.campaignId) return null;

        return {
            experimentTracking,
            salesforceResponse: {
                products: getUniqueTrackingProducts(trackingResultsByRecipient, recipientIds),
            },
        };
    }, [completedRecipientCount, recipientIdsKey, trackingResultsByRecipient]);

    useClickStreamCustomEventsTracking({
        experimentTracking: impressionTracking?.experimentTracking,
        isFireImpression: Boolean(impressionTracking?.experimentTracking?.campaignId),
        page: { type: 'gift-list' },
        salesforceResponse: impressionTracking?.salesforceResponse,
    });

    const handleTrackingReady = useCallback(({ experimentTracking, products, recipientItemId }) => {
        if (!recipientItemId) return;

        setTrackingResultsByRecipient((prev) => ({
            ...prev,
            [recipientItemId]: { experimentTracking, products },
        }));
    }, []);

    const occasionName = occasions.find((o) => o.id === occasionId)?.name?.toLowerCase() || '';

    return (
        <>
            {recipientList.map((recipient) => (
                <RecipientRecommendationLoader
                    key={recipient.id}
                    occasionId={occasionId}
                    occasionName={occasionName}
                    onTrackingReady={handleTrackingReady}
                    recipient={recipient}
                />
            ))}
        </>
    );
};

WithRegisteredRecommendations.defaultProps = {
    occasionId: '',
    occasions: [],
    recipients: {},
};

WithRegisteredRecommendations.propTypes = {
    occasionId: string,
    occasions: array,
    recipients: object,
};

export default WithRegisteredRecommendations;
